import React, { useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL } from './api';
import { readOfficeMapSettingsCache } from './officeMapSettingsCache';
import { loadGoogleMapsApi as loadSharedGoogleMapsApi, requestProjectRoute, isTrainOnlyRoute, drawProjectRoute } from './googleMapsRouting';

const GOOGLE_MAPS_API_KEY = 'AIzaSyB8aHKsvXwcOfduoR86RY1jNw1nAK_xn34';
const FALLBACK_CENTER = { lat: 54.8, lng: -3.1 };
const geocodeCache = new Map();
const OFFICE_MAP_SETTINGS_UPDATED_EVENT = 'monsurv:office-map-settings-updated';
function normaliseProject(project) {
    const lat = Number(project?.lat ?? project?.latitude ?? project?.site_lat ?? project?.site_lng_lat);
    const lng = Number(project?.lng ?? project?.longitude ?? project?.lon ?? project?.site_lng ?? project?.site_longitude);
    const address = String(project?.address || project?.site_address || '').trim();
    const name = String(project?.name || project?.project_name || project?.reference || '').trim();
    return {
        name,
        address,
        lat: Number.isFinite(lat) ? lat : null,
        lng: Number.isFinite(lng) ? lng : null
    };
}

function dedupeLocations(items) {
    const out = [];
    const seen = new Set();
    for (const item of items || []) {
        const lat = Number(item?.lat);
        const lng = Number(item?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
        const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ ...item, lat, lng });
    }
    return out;
}

function getAuthHeaderOnly() {
    try {
        const token = String(localStorage.getItem('monsurv_auth_token') || '').trim();
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

function normalizeOfficeMapOffice(raw, index) {
    const value = raw && typeof raw === 'object' ? raw : {};
    return {
        id: String(value.id || `office_${index + 1}`).trim() || `office_${index + 1}`,
        office_location: String(value.office_location || '').trim(),
        address: String(value.address || '').trim(),
        pin_color: String(value.pin_color || '#0f3b68').trim() || '#0f3b68'
    };
}

function normalizeOfficeMapSettings(raw) {
    const value = raw && typeof raw === 'object' ? raw : {};
    const legacyOffices = (value.office_location || value.address || value.pin_color)
        ? [value]
        : [];
    const offices = (Array.isArray(value.offices) ? value.offices : legacyOffices)
        .map((office, index) => normalizeOfficeMapOffice(office, index))
        .filter((office) => office.office_location || office.address);
    const primaryOfficeId = offices.some((office) => office.id === value.primary_office_id)
        ? String(value.primary_office_id)
        : (offices[0]?.id || '');
    const primaryOffice = offices.find((office) => office.id === primaryOfficeId) || offices[0] || null;
    return {
        offices,
        primary_office_id: primaryOfficeId,
        office_location: String(primaryOffice?.office_location || '').trim(),
        address: String(primaryOffice?.address || '').trim(),
        pin_color: String(primaryOffice?.pin_color || '#0f3b68').trim() || '#0f3b68',
        brand_upload_url: String(value.brand_upload_url || '').trim()
    };
}

function choosePreferredOfficeMapSettings(serverSettings, cachedSettings) {
    const server = normalizeOfficeMapSettings(serverSettings || null);
    const cached = cachedSettings ? normalizeOfficeMapSettings(cachedSettings) : null;
    if (!cached) return server;
    const serverCount = Array.isArray(server?.offices) ? server.offices.length : 0;
    const cachedCount = Array.isArray(cached?.offices) ? cached.offices.length : 0;
    if (cachedCount > serverCount) return { ...server, ...cached, brand_upload_url: cached.brand_upload_url || server.brand_upload_url || '' };
    return server;
}

function escapeHtml(value) {
    return String(value || '').replace(/[&<>"']/g, (char) => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
    }[char] || char));
}

function resolveUploadUrl(url) {
    const raw = String(url || '').trim();
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    return `${API_BASE_URL}${raw.startsWith('/') ? raw : `/${raw}`}`;
}

function buildDirectionsUrl({ destinationAddress, destinationLat, destinationLng, originAddress, travelMode = 'driving' } = {}) {
    const destination = String(destinationAddress || '').trim() || (
        Number.isFinite(Number(destinationLat)) && Number.isFinite(Number(destinationLng))
            ? `${Number(destinationLat)},${Number(destinationLng)}`
            : ''
    );
    if (!destination) return '';
    const params = new URLSearchParams();
    params.set('destination', destination);
    params.set('travelmode', String(travelMode || 'driving').trim() || 'driving');
    const origin = String(originAddress || '').trim();
    if (origin) params.set('origin', origin);
    return `https://www.google.com/maps/dir/?api=1&${params.toString()}`;
}

function loadGoogleMapsApi() {
    return loadSharedGoogleMapsApi(GOOGLE_MAPS_API_KEY);
}

async function geocodeWithGoogle(geocoder, project) {
    const address = String(project?.address || '').trim();
    if (!address) return null;

    const cacheKey = address.toLowerCase();
    if (geocodeCache.has(cacheKey)) {
        const cached = geocodeCache.get(cacheKey);
        return cached ? { ...project, ...cached } : null;
    }

    try {
        const result = await geocoder.geocode({ address: `${address}, UK`, region: 'GB' });
        const first = Array.isArray(result?.results) ? result.results[0] : null;
        const location = first?.geometry?.location;
        const lat = typeof location?.lat === 'function' ? Number(location.lat()) : Number(location?.lat);
        const lng = typeof location?.lng === 'function' ? Number(location.lng()) : Number(location?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            geocodeCache.set(cacheKey, null);
            return null;
        }
        const coords = { lat, lng };
        geocodeCache.set(cacheKey, coords);
        return { ...project, ...coords };
    } catch {
        geocodeCache.set(cacheKey, null);
        return null;
    }
}

export default function UKProjectMap({ projects, showDirections = false, routeMode = 'driving', officeSettings = null, fillHeight = false, minHeight = 210 }) {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markersRef = useRef([]);
    const directionsRendererRef = useRef(null);
    const overlayViewRef = useRef(null);
    const mapListenersRef = useRef([]);
    const hideTimeoutRef = useRef(null);
    const fadeTimeoutRef = useRef(null);
    const activeCardRef = useRef(null);
    const cardHoverRef = useRef(false);
    const scheduleHideRef = useRef(() => { });
    const [officeMapSettings, setOfficeMapSettings] = useState(() => normalizeOfficeMapSettings(officeSettings));
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [routeWarning, setRouteWarning] = useState('');
    const [hoverCard, setHoverCard] = useState(null);
    const [hoverCardVisible, setHoverCardVisible] = useState(false);

    const normalizedProjects = useMemo(() => {
        return Array.isArray(projects) ? projects.map(normaliseProject) : [];
    }, [projects]);

    useEffect(() => {
        let cancelled = false;

        async function loadOfficeSettings() {
            if (officeSettings) {
                if (!cancelled) setOfficeMapSettings(normalizeOfficeMapSettings(officeSettings));
                return;
            }
            try {
                const resp = await fetch(`${API_BASE_URL}/settings/office-map`, {
                    method: 'GET',
                    headers: getAuthHeaderOnly()
                });
                const json = await resp.json().catch(() => ({}));
                if (!resp.ok) throw new Error(json?.error || 'Failed to load office map settings');
                if (!cancelled) setOfficeMapSettings(choosePreferredOfficeMapSettings(json?.settings || {}, readOfficeMapSettingsCache()));
            } catch {
                if (!cancelled) setOfficeMapSettings(choosePreferredOfficeMapSettings(null, readOfficeMapSettingsCache()));
            }
        }

        loadOfficeSettings();

        function handleOfficeMapSettingsUpdated(event) {
            if (cancelled || officeSettings) return;
            const nextSettings = event?.detail?.settings;
            setOfficeMapSettings(normalizeOfficeMapSettings(nextSettings || null));
        }

        window.addEventListener(OFFICE_MAP_SETTINGS_UPDATED_EVENT, handleOfficeMapSettingsUpdated);
        return () => {
            cancelled = true;
            window.removeEventListener(OFFICE_MAP_SETTINGS_UPDATED_EVENT, handleOfficeMapSettingsUpdated);
        };
    }, [officeSettings]);

    useEffect(() => {
        let cancelled = false;

        async function resolveLocations() {
            setLoading(true);
            setError('');

            try {
                const maps = await loadGoogleMapsApi();
                const geocoder = new maps.Geocoder();

                const direct = normalizedProjects.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
                const missing = normalizedProjects.filter((p) => !Number.isFinite(p.lat) || !Number.isFinite(p.lng));

                const geocoded = await Promise.all(missing.map((project) => geocodeWithGoogle(geocoder, project)));
                if (cancelled) return;

                const nextLocations = dedupeLocations([...direct, ...geocoded.filter(Boolean)]);
                setLocations(nextLocations);
                if (!nextLocations.length && normalizedProjects.length) {
                    setError('Map is temporarily unavailable for the current project locations.');
                }
            } catch (e) {
                if (!cancelled) {
                    setLocations([]);
                    setError(e?.message || 'Google Maps is temporarily unavailable.');
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        resolveLocations();
        return () => {
            cancelled = true;
        };
    }, [normalizedProjects]);

    useEffect(() => {
        if (!mapRef.current || loading || error) return;
        let cancelled = false;

        async function renderMap() {
            setRouteWarning('');
            const maps = await loadGoogleMapsApi();
            if (cancelled || !mapRef.current) return;

            if (!mapInstanceRef.current) {
                mapInstanceRef.current = new maps.Map(mapRef.current, {
                    center: FALLBACK_CENTER,
                    zoom: 5,
                    mapTypeControl: false,
                    streetViewControl: false,
                    fullscreenControl: false,
                    gestureHandling: 'greedy',
                    clickableIcons: false,
                    styles: [
                        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                        { featureType: 'transit', stylers: [{ visibility: 'off' }] }
                    ]
                });
                overlayViewRef.current = new maps.OverlayView();
                overlayViewRef.current.onAdd = () => { };
                overlayViewRef.current.draw = () => { };
                overlayViewRef.current.onRemove = () => { };
                overlayViewRef.current.setMap(mapInstanceRef.current);
            }

            const buildPinMarkerIcon = ({ label = 'M', color = '#0f3b68', textColor = '#ffffff' } = {}) => {
                const svg = [
                    '<svg xmlns="http://www.w3.org/2000/svg" width="44" height="56" viewBox="0 0 44 56">',
                    '<defs>',
                    '  <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">',
                    '    <feDropShadow dx="0" dy="3" stdDeviation="3" flood-color="rgba(15,23,42,0.28)"/>',
                    '  </filter>',
                    '</defs>',
                    '<g filter="url(#shadow)">',
                    `  <path d="M22 52C22 52 8 36.5 8 24C8 15.1634 14.7157 8 22 8C29.2843 8 36 15.1634 36 24C36 36.5 22 52 22 52Z" fill="${escapeHtml(color)}" stroke="#ffffff" stroke-width="2"/>`,
                    '  <circle cx="22" cy="24" r="11" fill="rgba(255,255,255,0.14)"/>',
                    `  <text x="22" y="29" text-anchor="middle" font-family="Segoe UI, Arial, sans-serif" font-size="15" font-weight="700" fill="${escapeHtml(textColor)}">${escapeHtml(String(label || 'M').slice(0, 2).toUpperCase())}</text>`,
                    '</g>',
                    '</svg>'
                ].join('');
                return {
                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
                    scaledSize: new maps.Size(44, 56),
                    anchor: new maps.Point(22, 52)
                };
            };

            for (const marker of markersRef.current) {
                marker.setMap(null);
            }
            markersRef.current = [];

            if (directionsRendererRef.current) {
                directionsRendererRef.current.setMap(null);
                directionsRendererRef.current = null;
            }

            for (const listener of mapListenersRef.current) {
                listener?.remove?.();
            }
            mapListenersRef.current = [];

            const clearHideTimers = () => {
                if (hideTimeoutRef.current) {
                    clearTimeout(hideTimeoutRef.current);
                    hideTimeoutRef.current = null;
                }
                if (fadeTimeoutRef.current) {
                    clearTimeout(fadeTimeoutRef.current);
                    fadeTimeoutRef.current = null;
                }
            };

            const getCardPlacement = (position) => {
                const projection = overlayViewRef.current?.getProjection?.();
                const container = mapRef.current;
                if (!projection || !container || !position) return null;

                const point = projection.fromLatLngToContainerPixel(new maps.LatLng(Number(position.lat), Number(position.lng)));
                if (!point) return null;

                const cardWidth = 236;
                const cardHeight = 136;
                const pointerHeight = 12;
                const gap = 8;
                const bounds = container.getBoundingClientRect();
                const maxLeft = Math.max(12, bounds.width - cardWidth - 12);
                const left = Math.min(Math.max(12, point.x - (cardWidth / 2)), maxLeft);
                const topAbove = point.y - cardHeight - pointerHeight - gap;
                const topBelow = point.y + pointerHeight + gap;
                const maxTop = Math.max(12, bounds.height - cardHeight - 12);
                const placement = topAbove >= 12 ? 'above' : 'below';
                const top = placement === 'above'
                    ? topAbove
                    : Math.min(Math.max(12, topBelow), maxTop);

                const pointerLeft = Math.min(Math.max(24, point.x - left - 10), cardWidth - 24);

                return { left, top, placement, pointerLeft };
            };

            const updateHoverCardPosition = () => {
                const activeCard = activeCardRef.current;
                if (!activeCard) return;
                const placement = getCardPlacement(activeCard.position);
                if (!placement) return;
                setHoverCard((current) => {
                    if (!current || current.id !== activeCard.id) return current;
                    return { ...current, ...placement };
                });
            };

            const hideHoverCardImmediately = () => {
                clearHideTimers();
                cardHoverRef.current = false;
                activeCardRef.current = null;
                setHoverCardVisible(false);
                setHoverCard(null);
            };

            const scheduleHoverCardHide = () => {
                clearHideTimers();
                if (!activeCardRef.current || cardHoverRef.current) return;
                hideTimeoutRef.current = setTimeout(() => {
                    if (!activeCardRef.current || cardHoverRef.current) return;
                    setHoverCardVisible(false);
                    fadeTimeoutRef.current = setTimeout(() => {
                        if (cardHoverRef.current) return;
                        activeCardRef.current = null;
                        setHoverCard(null);
                    }, 180);
                }, 2000);
            };

            const showHoverCard = (cardData) => {
                clearHideTimers();
                cardHoverRef.current = false;
                const placement = getCardPlacement(cardData?.position);
                const nextCard = {
                    ...cardData,
                    left: placement?.left ?? 12,
                    top: placement?.top ?? 12,
                    placement: placement?.placement ?? 'above',
                    pointerLeft: placement?.pointerLeft ?? 24
                };
                activeCardRef.current = nextCard;
                setHoverCard(nextCard);
                setHoverCardVisible(true);
            };

            scheduleHideRef.current = scheduleHoverCardHide;

            const map = mapInstanceRef.current;
            const officeEntries = Array.isArray(officeMapSettings?.offices) && officeMapSettings.offices.length
                ? officeMapSettings.offices
                : (officeMapSettings?.address || officeMapSettings?.office_location
                    ? [{
                        id: String(officeMapSettings?.primary_office_id || 'office_1').trim() || 'office_1',
                        office_location: String(officeMapSettings?.office_location || '').trim(),
                        address: String(officeMapSettings?.address || '').trim(),
                        pin_color: String(officeMapSettings?.pin_color || '#0f3b68').trim() || '#0f3b68'
                    }]
                    : []);
            const activeOriginOffice = officeEntries.find((office) => office.id === officeMapSettings?.primary_office_id) || officeEntries[0] || null;
            const officeAddress = String(activeOriginOffice?.address || '').trim();
            const officeBrandUrl = resolveUploadUrl(officeMapSettings?.brand_upload_url || '');
            let officePosition = null;

            const bounds = new maps.LatLngBounds();
            let hasBounds = false;

            if (officeEntries.length) {
                const officeGeocoder = new maps.Geocoder();
                for (const office of officeEntries) {
                    const nextOfficeAddress = String(office?.address || '').trim();
                    const nextOfficeLocation = String(office?.office_location || '').trim();
                    const nextOfficePinColor = String(office?.pin_color || '#0f3b68').trim() || '#0f3b68';
                    if (!nextOfficeAddress) continue;
                    try {
                        const officeResult = await officeGeocoder.geocode({ address: `${nextOfficeAddress}, UK`, region: 'GB' });
                        if (cancelled) return;
                        const officeMatch = Array.isArray(officeResult?.results) ? officeResult.results[0] : null;
                        const officeLoc = officeMatch?.geometry?.location;
                        const officeLat = typeof officeLoc?.lat === 'function' ? Number(officeLoc.lat()) : Number(officeLoc?.lat);
                        const officeLng = typeof officeLoc?.lng === 'function' ? Number(officeLoc.lng()) : Number(officeLoc?.lng);
                        if (!Number.isFinite(officeLat) || !Number.isFinite(officeLng)) continue;

                        const position = { lat: officeLat, lng: officeLng };
                        if (activeOriginOffice && office.id === activeOriginOffice.id) {
                            officePosition = position;
                        }
                        const officeCardId = `office:${office.id}:${nextOfficeLocation || nextOfficeAddress || 'monsurv'}`;
                        const officeMarker = new maps.Marker({
                            position,
                            map,
                            title: nextOfficeLocation || 'Monsurv Office',
                            icon: officeBrandUrl
                                ? { url: officeBrandUrl, scaledSize: new maps.Size(44, 44), anchor: new maps.Point(22, 22) }
                                : buildPinMarkerIcon({ label: (nextOfficeLocation.match(/[A-Za-z0-9]/)?.[0] || 'M').toUpperCase(), color: nextOfficePinColor })
                        });
                        officeMarker.addListener('mouseover', () => {
                            showHoverCard({
                                id: officeCardId,
                                title: nextOfficeLocation || 'Monsurv Office',
                                address: nextOfficeAddress,
                                position,
                                links: []
                            });
                        });
                        officeMarker.addListener('mouseout', () => {
                            if (activeCardRef.current?.id === officeCardId && !cardHoverRef.current) {
                                scheduleHoverCardHide();
                            }
                        });
                        officeMarker.addListener('click', () => {
                            showHoverCard({
                                id: officeCardId,
                                title: nextOfficeLocation || 'Monsurv Office',
                                address: nextOfficeAddress,
                                position,
                                links: []
                            });
                        });
                        markersRef.current.push(officeMarker);
                        bounds.extend(position);
                        hasBounds = true;
                    } catch {
                        // ignore office marker failures
                    }
                }
            }

            if (!locations.length) {
                hideHoverCardImmediately();
                if (hasBounds) {
                    if (officeEntries.length === 1) {
                        map.setCenter(bounds.getCenter());
                        map.setZoom(7);
                    } else {
                        map.fitBounds(bounds, 48);
                    }
                } else {
                    map.setCenter(FALLBACK_CENTER);
                    map.setZoom(5);
                }
                return;
            }

            for (const location of locations) {
                const position = { lat: Number(location.lat), lng: Number(location.lng) };
                const title = location.name || location.address || 'Project site';
                const cardId = `project:${title}:${position.lat.toFixed(6)}:${position.lng.toFixed(6)}`;
                const directionsLinks = [
                    {
                        label: 'Driving',
                        url: buildDirectionsUrl({
                            destinationAddress: location.address,
                            destinationLat: position.lat,
                            destinationLng: position.lng,
                            originAddress: officeAddress,
                            travelMode: 'driving'
                        })
                    },
                    {
                        label: 'Transit',
                        url: buildDirectionsUrl({
                            destinationAddress: location.address,
                            destinationLat: position.lat,
                            destinationLng: position.lng,
                            originAddress: officeAddress,
                            travelMode: 'transit'
                        })
                    }
                ].filter((item) => item.url);

                const marker = new maps.Marker({
                    position,
                    map,
                    title,
                    icon: buildPinMarkerIcon({ label: 'M', color: '#0f3b68' })
                });

                marker.addListener('mouseover', () => {
                    showHoverCard({
                        id: cardId,
                        title,
                        address: location.address,
                        position,
                        links: directionsLinks
                    });
                });

                marker.addListener('mouseout', () => {
                    if (activeCardRef.current?.id === cardId && !cardHoverRef.current) {
                        scheduleHoverCardHide();
                    }
                });

                marker.addListener('click', () => {
                    showHoverCard({
                        id: cardId,
                        title,
                        address: location.address,
                        position,
                        links: directionsLinks
                    });
                });

                markersRef.current.push(marker);
                bounds.extend(position);
                hasBounds = true;
            }

            mapListenersRef.current = [
                maps.event.addListener(map, 'bounds_changed', updateHoverCardPosition),
                maps.event.addListener(map, 'zoom_changed', updateHoverCardPosition),
                maps.event.addListener(map, 'center_changed', updateHoverCardPosition)
            ];

            const requestsDirections = showDirections && locations.length === 1 && (routeMode === 'driving' || routeMode === 'transit');
            if (requestsDirections && !officePosition) {
                setRouteWarning('Route unavailable: the origin office could not be located. Showing project pins only.');
            }
            const wantsDirections = requestsDirections && officePosition;
            if (wantsDirections) {
                try {
                    const destination = locations[0];
                    const route = await requestProjectRoute({
                        origin: officeAddress || officePosition,
                        destination: destination.address || { lat: Number(destination.lat), lng: Number(destination.lng) },
                        travelMode: routeMode,
                        includePath: true,
                        legacyKey: GOOGLE_MAPS_API_KEY
                    });
                    if (cancelled) return;
                    if (routeMode === 'transit' && !isTrainOnlyRoute(route)) {
                        throw new Error('No train-only route was found.');
                    }

                    directionsRendererRef.current = drawProjectRoute({
                        maps, map, result: route,
                        polylineOptions: {
                            strokeColor: routeMode === 'transit' ? '#7c3aed' : '#2563eb',
                            strokeOpacity: 0.86,
                            strokeWeight: routeMode === 'transit' ? 5 : 6
                        }
                    });
                    const routeBounds = directionsRendererRef.current.bounds;
                    if (routeBounds) {
                        map.fitBounds(routeBounds, 56);
                        return;
                    }
                } catch (e) {
                    if (cancelled) return;
                    setRouteWarning(`Route unavailable. Showing location pins only. ${e?.message || 'Travel calculation failed.'}`);
                }
            }

            if (locations.length === 1) {
                map.setCenter(bounds.getCenter());
                map.setZoom(10);
                return;
            }

            map.fitBounds(bounds, 48);
        }

        renderMap().catch((e) => {
            if (!cancelled) setError(e?.message || 'Google Maps is temporarily unavailable.');
        });

        return () => {
            cancelled = true;
            for (const listener of mapListenersRef.current) {
                listener?.remove?.();
            }
            mapListenersRef.current = [];
        };
    }, [locations, loading, error, officeMapSettings, showDirections, routeMode]);

    useEffect(() => {
        return () => {
            if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
            if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
            if (directionsRendererRef.current) directionsRendererRef.current.setMap(null);
        };
    }, []);

    return (
        <div
            style={{
                width: '100%',
                ...(fillHeight
                    ? { height: '100%', minHeight, margin: 0 }
                    : { aspectRatio: '16 / 9', height: 'auto', minHeight, maxHeight: 280, margin: '8px 0 0 0' }),
                borderRadius: 12,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                background: '#eaf3ff',
                position: 'relative'
            }}
        >
            {loading ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading mapG��</div>
            ) : null}

            {!loading && error ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b91c1c', padding: 16, textAlign: 'center' }}>
                    {error}
                </div>
            ) : null}

            {!error ? (
                <div
                    ref={mapRef}
                    aria-label="UK Project Map"
                    style={{
                        flex: 1,
                        width: '100%',
                        height: '100%',
                        minHeight,
                        display: loading ? 'none' : 'block',
                        background: '#eaf3ff'
                    }}
                />
            ) : null}
            {!error && !loading && routeWarning ? (
                <div role="status" style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '8px 12px', background: '#fff7ed', color: '#9a3412', fontSize: 12 }}>
                    {routeWarning}
                </div>
            ) : null}

            {hoverCard ? (
                <div
                    onMouseEnter={() => {
                        cardHoverRef.current = true;
                        if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
                        if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
                        hideTimeoutRef.current = null;
                        fadeTimeoutRef.current = null;
                        setHoverCardVisible(true);
                    }}
                    onMouseLeave={() => {
                        cardHoverRef.current = false;
                        scheduleHideRef.current();
                    }}
                    style={{
                        position: 'absolute',
                        left: hoverCard.left,
                        top: hoverCard.top,
                        width: 236,
                        padding: '12px 14px',
                        borderRadius: 14,
                        background: 'rgba(255,255,255,0.98)',
                        boxShadow: '0 14px 30px rgba(15, 23, 42, 0.18)',
                        border: '1px solid rgba(148, 163, 184, 0.28)',
                        fontFamily: 'Segoe UI, Arial, sans-serif',
                        opacity: hoverCardVisible ? 1 : 0,
                        transform: hoverCardVisible ? 'translateY(0)' : 'translateY(6px)',
                        transition: 'opacity 180ms ease, transform 180ms ease',
                        pointerEvents: hoverCardVisible ? 'auto' : 'none',
                        zIndex: 3
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            left: hoverCard.pointerLeft,
                            width: 18,
                            height: 18,
                            background: 'rgba(255,255,255,0.98)',
                            borderLeft: '1px solid rgba(148, 163, 184, 0.28)',
                            borderTop: '1px solid rgba(148, 163, 184, 0.28)',
                            transform: 'rotate(45deg)',
                            boxShadow: '-4px -4px 12px rgba(15, 23, 42, 0.04)',
                            top: hoverCard.placement === 'above' ? 'calc(100% - 10px)' : -8
                        }}
                    />
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{hoverCard.title || 'Project site'}</div>
                    {hoverCard.address ? (
                        <div style={{ marginTop: 5, fontSize: 12, lineHeight: 1.45, color: '#475569' }}>{hoverCard.address}</div>
                    ) : null}
                    {Array.isArray(hoverCard.links) && hoverCard.links.length ? (
                        <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                            {hoverCard.links.map((link) => (
                                <a
                                    key={link.label}
                                    href={link.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 700,
                                        color: '#0f62fe',
                                        textDecoration: 'none'
                                    }}
                                >
                                    {link.label}
                                </a>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
